import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { parseError } from "@/lib/error-utils";
import { isNetworkError, toResilientError, type ResilientFetchError } from "@/lib/resilientFetch";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Estados de resiliência para auth
  const [authError, setAuthError] = useState<string | null>(null);
  const [authErrorCode, setAuthErrorCode] = useState<string | null>(null);
  const [isRetryableAuthError, setIsRetryableAuthError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isReconnecting, setIsReconnecting] = useState(false);

  // Referência para última ação (para retry)
  const lastActionRef = useRef<null | (() => Promise<void>)>(null);
  const lastCredentialsRef = useRef<{ email: string; password: string } | null>(null);

  useEffect(() => {
    let isMounted = true;
    let isInitializing = true;

    // 1. Listener para eventos CONTÍNUOS (não controla loading após inicialização)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      
      // Durante inicialização, ignorar - getSession vai cuidar
      if (isInitializing) return;
      
      // Após inicialização, apenas atualizar estado (sem tocar em loading)
      setSession(session);
      setUser(session?.user ?? null);
    });

    // 2. Inicialização (CONTROLA o loading - só acontece uma vez)
    const initialize = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
      } catch (error) {
        console.error("Erro ao inicializar auth:", error);
      } finally {
        isInitializing = false;
        if (isMounted) setLoading(false);
      }
    };

    initialize();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleAuthInfraError = useCallback((err: unknown) => {
    const isNetwork = isNetworkError(err);
    const e = err as Partial<ResilientFetchError>;
    
    let message = "Servidor temporariamente indisponível. Tente novamente em instantes.";
    let code = "AUTH_UNAVAILABLE";
    let retryable = true;

    // Verificar se é erro de offline
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      message = "Você está sem conexão com a internet.";
      code = "OFFLINE";
      retryable = true;
    } else if (e?.code) {
      code = e.code;
      message = e.message ?? message;
      retryable = e.isRetryable ?? isNetwork;
    } else if (isNetwork) {
      // Erro de rede genérico
      const errMsg = err instanceof Error ? err.message : String(err);
      if (errMsg.toLowerCase().includes("503") || errMsg.toLowerCase().includes("unavailable")) {
        message = "Servidor temporariamente indisponível. Tentando reconectar…";
        code = "SERVICE_UNAVAILABLE";
      } else if (errMsg.toLowerCase().includes("timeout")) {
        message = "Servidor demorou para responder. Tente novamente.";
        code = "TIMEOUT";
      } else {
        message = "Falha de conexão com o servidor. Tente novamente.";
        code = "NETWORK";
      }
    }

    setAuthError(message);
    setAuthErrorCode(code);
    setIsRetryableAuthError(retryable);
  }, []);

  const clearAuthError = useCallback(() => {
    setAuthError(null);
    setAuthErrorCode(null);
    setIsRetryableAuthError(false);
    setRetryCount(0);
    setIsReconnecting(false);
  }, []);

  const signUp = async (email: string, password: string, nome: string, company?: string) => {
    try {
      clearAuthError();
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            nome,
            company,
          },
        },
      });

      if (error) throw error;

      toast({
        title: "Conta criada!",
        description: "Bem-vindo à Vendas.Group!",
      });

      return { data, error: null };
    } catch (error: unknown) {
      // Verificar se é erro de infraestrutura
      if (isNetworkError(error)) {
        handleAuthInfraError(error);
        return { data: null, error };
      }

      const parsed = parseError(error);
      toast({
        title: "Erro ao criar conta",
        description: `${parsed.message}${parsed.suggestion ? ` - ${parsed.suggestion}` : ''}`,
        variant: "destructive",
      });
      return { data: null, error };
    }
  };

  const signIn = useCallback(async (email: string, password: string) => {
    clearAuthError();
    setIsReconnecting(true);
    lastCredentialsRef.current = { email, password };

    const performSignIn = async () => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast({
        title: "Login realizado!",
        description: "Bem-vindo de volta!",
      });

      return { data, error: null };
    };

    lastActionRef.current = async () => {
      await performSignIn();
    };

    try {
      const result = await performSignIn();
      setIsReconnecting(false);
      return result;
    } catch (error: unknown) {
      setIsReconnecting(false);

      // Verificar se é erro de credenciais inválidas
      const errMsg = error instanceof Error ? error.message : String(error);
      const looksLikeBadCreds =
        errMsg.toLowerCase().includes("invalid") ||
        errMsg.toLowerCase().includes("credentials") ||
        errMsg.toLowerCase().includes("senha") ||
        errMsg.toLowerCase().includes("email") ||
        errMsg.toLowerCase().includes("invalid login credentials");

      if (looksLikeBadCreds && !isNetworkError(error)) {
        setAuthError("E-mail ou senha inválidos.");
        setAuthErrorCode("BAD_CREDENTIALS");
        setIsRetryableAuthError(false);
        toast({
          title: "Erro ao fazer login",
          description: "E-mail ou senha inválidos.",
          variant: "destructive",
        });
        return { data: null, error };
      }

      // Verificar se é erro de infraestrutura
      if (isNetworkError(error)) {
        handleAuthInfraError(error);
        return { data: null, error };
      }

      // Outro erro
      const parsed = parseError(error);
      toast({
        title: "Erro ao fazer login",
        description: `${parsed.message}${parsed.suggestion ? ` - ${parsed.suggestion}` : ''}`,
        variant: "destructive",
      });
      return { data: null, error };
    }
  }, [toast, clearAuthError, handleAuthInfraError]);

  const retryNow = useCallback(async () => {
    if (!lastCredentialsRef.current) return { data: null, error: new Error("No credentials") };
    
    setRetryCount((c) => c + 1);
    setIsReconnecting(true);
    setAuthError(null);

    try {
      const { email, password } = lastCredentialsRef.current;
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      setIsReconnecting(false);
      clearAuthError();
      
      toast({
        title: "Login realizado!",
        description: "Bem-vindo de volta!",
      });

      return { data, error: null };
    } catch (error: unknown) {
      setIsReconnecting(false);
      
      // Verificar se é erro de credenciais
      const errMsg = error instanceof Error ? error.message : String(error);
      const looksLikeBadCreds =
        errMsg.toLowerCase().includes("invalid") ||
        errMsg.toLowerCase().includes("credentials");

      if (looksLikeBadCreds && !isNetworkError(error)) {
        setAuthError("E-mail ou senha inválidos.");
        setAuthErrorCode("BAD_CREDENTIALS");
        setIsRetryableAuthError(false);
        return { data: null, error };
      }

      handleAuthInfraError(error);
      return { data: null, error };
    }
  }, [toast, clearAuthError, handleAuthInfraError]);

  const signOut = useCallback(async () => {
    try {
      clearAuthError();
      // Usar scope: "global" para encerrar todas as sessões
      const { error } = await supabase.auth.signOut({ scope: "global" });
      if (error) throw error;

      toast({
        title: "Logout realizado",
        description: "Até logo!",
      });
    } catch (error: unknown) {
      // Mesmo com erro, garantir que o logout local aconteça
      console.error("Erro no logout:", error);
      const errMsg = error instanceof Error ? error.message : "Erro desconhecido";
      toast({
        title: "Erro ao fazer logout",
        description: errMsg,
        variant: "destructive",
      });
      // Re-throw para que handleLogout saiba que houve erro
      throw error;
    }
  }, [toast, clearAuthError]);

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    // Estados de resiliência
    authError,
    authErrorCode,
    isRetryableAuthError,
    retryCount,
    isReconnecting,
    retryNow,
    clearAuthError,
  };
}
